// Kickoff & Packets (S12, D17): the packet chain Packet Draft -> Generated -> Reviewed -> Released as a journey
// (generation is not a button, DC-124), the coverage validator that blocks release on incomplete coverage, the packet
// table and the panel acknowledgment summary. The Recruiter reviews and releases; release opens the Release packets
// dialog. Seats trim the page (DC-129, D13): the server's `projection` says which parts this viewer sees.
//
//   full             owner, grantee, Admin, TA Manager: everything, with packet links, Review and Release
//   plan             Hiring Manager: rounds, assignments, packet statuses, acknowledgment summary; no links, no actions
//   finalized_panel  HR Business Partner: rounds, assignments, release state; no links, no acknowledgment detail
//   release_ack      Coordinator: journey, email status, acknowledgment summary; no packet table
//
// KickoffPacketsView is pure (SSR-tested); KickoffPackets is the data wrapper over the tool door.

import { useState } from "react";
import {
  ACTION_LABELS, AckSummary, Banner, Button, DOCUMENT_LABELS, G1_LABELS, G7_TEMPLATES, Gate, Journey, LABEL_TEMPLATES,
  McRoot, PACKET_STATUS, PageHeader, Pill, ROOTS_LABELS, RootsHeat, SKILL_N, SLIDE_LABELS, SURFACE_LABELS, Table, Td, Th, Tr, toneOf,
  ROLE_LABELS, useHs, type AckSummaryRow, type GateRow, type JourneyStep, type ResponseStatus, type RootsHeatItem, type RootsValue,
} from "@/mc";
import { forbiddenHits } from "@/mc/labels/forbidden";
import {
  packetDraftKickoff, packetReadinessCheck, packetRelease, packetReview, panelAckSummary, type PacketDraftKickoffOutput,
} from "@/api/tools";
import { errorText, useWrite } from "@/api/useTool";
import { useSettledRead } from "../shared/settledRead";
import { PanelChangeSlideOver } from "@/views/panel/PanelChangeSlideOver";
import { KICKOFF_LABELS } from "./labels";
import { ReleaseDialog, type ReleaseRecipient } from "./ReleaseDialog";
import "./panel.css";

export type KickoffProjection = "full" | "plan" | "finalized_panel" | "release_ack";
export type ReleaseStatus = "draft" | "generated" | "reviewed" | "released";

export interface KickoffRow {
  roundDefId: string;
  roundKind: string;
  /** The server's round label; drawn only for a round with no word of his own and only when it is clean. */
  roundLabel: string;
  interviewerId: string | null;
  interviewerName: string | null;
  values: readonly string[];
  skillNumbers: readonly number[];
  packetId: string | null;
  packetVersion: number | null;
  packetStatus: string;
}

export interface KickoffRelease {
  status: ReleaseStatus;
  version?: number | undefined;
  email?: { version: number; recipients: number; delivered: number } | null | undefined;
}

export interface KickoffPacketsViewProps {
  projection: KickoffProjection;
  rows: readonly KickoffRow[];
  blockers: readonly string[];
  release: KickoffRelease | null;
  ackRows?: readonly AckSummaryRow[] | null | undefined;
  loading?: boolean | undefined;
  onReview?: (() => void) | undefined;
  reviewing?: boolean | undefined;
  onRelease?: (() => void) | undefined;
  /** Opens the Panel change slide-over on a Final Interview Loop round (the Recruiter's and the Hiring Manager's views). */
  onPanelChange?: ((roundDefId: string) => void) | undefined;
  refused?: string | undefined;
}

const ROOTS_KEYS: readonly RootsValue[] = ["respect", "ownership", "opportunity", "together", "service"] as RootsValue[];
const CHAIN: readonly ReleaseStatus[] = ["draft", "generated", "reviewed", "released"];
const CHAIN_WORD = {
  draft: PACKET_STATUS.packetDraft, generated: PACKET_STATUS.generated, reviewed: PACKET_STATUS.reviewed, released: PACKET_STATUS.released,
} as const;

/** V1 packet statuses on his chain: generated -> Generated, approved -> Reviewed, issued/opened -> Released. */
export function packetWord(status: string) {
  if (status === "generated") return PACKET_STATUS.generated;
  if (status === "approved") return PACKET_STATUS.reviewed;
  if (status === "issued" || status === "opened") return PACKET_STATUS.released;
  return PACKET_STATUS.packetDraft;
}

/** His name for a round: the Recruiter screen, the HM interview, or Final Interview Loop · Round n for the loop. */
export function roundName(row: KickoffRow, loopIndex: number): string {
  if (row.roundKind === "recruiter_screen") return G1_LABELS.recruiterScreen;
  if (row.roundKind === "hm_round") return G1_LABELS.hmInterview;
  if (row.roundKind === "onsite") return LABEL_TEMPLATES.finalLoopRound({ n: loopIndex });
  return forbiddenHits(row.roundLabel).length === 0 ? row.roundLabel : LABEL_TEMPLATES.finalLoopRound({ n: loopIndex });
}

function assigned(row: KickoffRow): string {
  const values = row.values.filter((v): v is RootsValue => v in ROOTS_LABELS && v !== "roots").map((v) => ROOTS_LABELS[v]);
  return [...values, ...row.skillNumbers.map((n) => SKILL_N({ n }))].join(" · ");
}

export function KickoffPacketsView({
  projection, rows, blockers, release, ackRows, loading, onReview, reviewing, onRelease, onPanelChange, refused,
}: KickoffPacketsViewProps) {
  const status: ReleaseStatus = release?.status ?? "draft";
  const at = CHAIN.indexOf(status);
  const released = status === "released";
  const full = projection === "full";
  const steps: JourneyStep[] = CHAIN.map((s, i) => ({
    label: CHAIN_WORD[s],
    state: released || i < at ? "done" : i === at ? "active" : "next",
  }));

  let loop = 0;
  const named = rows.map((row) => {
    const onsite = row.roundKind === "onsite";
    return { row, name: roundName(row, onsite ? ++loop : loop + 1), loopN: onsite ? loop : null };
  });

  const gateRows: GateRow[] = blockers.map((text) => ({ status: "bad", text, hs: "pkt.blocker" }));
  if (gateRows.length === 0 && full && status !== "reviewed" && !released) {
    gateRows.push({ status: "todo", text: PACKET_STATUS.reviewed });
  }
  const covered = ROOTS_KEYS.filter((v) => rows.some((r) => r.values.includes(v)));
  const heat: RootsHeatItem[] = ROOTS_KEYS.map((value) => {
    const holding = named.filter(({ row }) => row.values.includes(value));
    return {
      value,
      rounds: holding.map(({ name }) => name),
      pct: holding.length > 0 ? 100 : 0,
      owners: holding.filter(({ row }) => row.interviewerName).map(({ row }) => ({ name: row.interviewerName as string, hueKey: row.interviewerId ?? undefined })),
    };
  });

  const showTable = projection !== "release_ack";
  const showPacket = projection === "full" || projection === "plan";
  const showAck = projection !== "finalized_panel" && !!ackRows;

  return (
    <div className="mc-kp">
      <PageHeader
        title={KICKOFF_LABELS.title}
        loading={loading}
        pills={<Pill tone={toneOf(CHAIN_WORD[status])}>{CHAIN_WORD[status]}</Pill>}
        actions={full && !released && onReview ? (
          <Button tone="ghost" busy={reviewing} onClick={onReview} 
            blocked={status !== "generated" ? { reason: blockers[0] ?? PACKET_STATUS.generated } : undefined}>
            {ACTION_LABELS.review}
          </Button>
        ) : undefined}
      />
      <Journey steps={steps} label={DOCUMENT_LABELS.interviewPacket} />
      {refused ? <Banner tone="red" live>{refused}</Banner> : null}
      {released ? (
        <Banner tone="ok">
          {DOCUMENT_LABELS.oneEmail}{release?.email ? ` ${G7_TEMPLATES.version({ n: release.email.version })}` : ""}
        </Banner>
      ) : null}
      {projection !== "release_ack" ? (
        <div className="mc-kp-cover">
          <Gate
            title={SURFACE_LABELS.coverageValidator}
            rows={gateRows}
            ring={{ n: covered.length, of: ROOTS_KEYS.length }}
            ready={gateRows.length === 0}
            loading={loading}
            mode={full && !released ? "edit" : "view"}
            action={full && !released && onRelease ? { label: ACTION_LABELS.release, onClick: onRelease, hs: "pkt.release" } : undefined}
          />
          <RootsHeat items={heat} />
        </div>
      ) : null}
      {projection !== "release_ack" && gateRows.length > 0 && full ? (
        <p className="mc-kp-rule">{KICKOFF_LABELS.blockRelease}</p>
      ) : null}
      {showTable ? (
        <Table label={KICKOFF_LABELS.packet} loading={loading} cols={showPacket ? 4 : 3}
          head={(
            <Tr>
              <Th>{KICKOFF_LABELS.round}</Th>
              <Th>{ROLE_LABELS.interviewer}</Th>
              <Th>{KICKOFF_LABELS.assignedValuesSkills}</Th>
              {showPacket ? <Th>{KICKOFF_LABELS.packet}</Th> : null}
            </Tr>
          )}>
          {named.map(({ row, name, loopN }, i) => {
            const word = packetWord(row.packetStatus);
            // the row's place in the plan: a round definition's id carries "_", which the hotspot grammar refuses
            return (
              <Tr key={row.roundDefId} hs={`pkt.row.${i + 1}`}>
                <Td sub={onPanelChange && loopN !== null ? (
                  <Button tone="ghost" onClick={() => onPanelChange(row.roundDefId)} >
                    {SLIDE_LABELS.panelChange}
                  </Button>
                ) : undefined}>{name}</Td>
                <Td>{row.interviewerName ?? ""}</Td>
                <Td>{assigned(row)}</Td>
                {showPacket ? (
                  <Td sub={full && row.packetId ? (
                    <PacketLink packetId={row.packetId} version={row.packetVersion} hs={`pkt.packet.${i + 1}`} />
                  ) : undefined}>
                    <Pill tone={toneOf(word)}>{word}</Pill>
                  </Td>
                ) : null}
              </Tr>
            );
          })}
        </Table>
      ) : null}
      {showAck ? <AckSummary rows={ackRows ?? []} loading={loading} /> : null}
    </div>
  );
}

/** The row's packet link; its hotspot is the row's place in the plan (`pkt.packet.<n>`), like the row's own. */
function PacketLink({ packetId, version, hs }: { packetId: string; version: number | null; hs: string }) {
  return (
    <a href={`#packets/${packetId}`} {...useHs(hs)}>
      {KICKOFF_LABELS.packetLink}{version ? ` · ${G7_TEMPLATES.version({ n: version })}` : ""}
    </a>
  );
}

// ---- the data wrapper ------------------------------------------------------------------------------------------------

type Readiness = Awaited<ReturnType<typeof packetReadinessCheck>>;
/** S10's additions to the readiness read (absent on an older server: the page falls back to the full projection). */
type ReadinessV2 = Readiness & {
  projection?: KickoffProjection;
  release?: { status: ReleaseStatus; version?: number; email?: { version: number; recipients: number; delivered: number } | null } | null;
  blockers?: string[];
};

const ACK_STATUS: Readonly<Record<string, ResponseStatus>> = { accepted: "accepted", declined: "declined", pending: "pending" };

/** The release dialog's draft: the body the release delivers to an overview recipient (the Recruiter, the HR Business
 *  Partner: every assignment, no link, no Accept / Decline line), as the server composed it, so the Recruiter reviews
 *  the text that is sent. The template-only body is the fallback while no overview delivery exists. */
const composedDraft = (data: PacketDraftKickoffOutput | undefined): { subject: string; body: string } | undefined => {
  if (!data) return undefined;
  const pick = (variant: string) => data.bodies.find((b) => b["variant"] === variant);
  const row = pick("overview") ?? pick("no_link");
  return {
    subject: typeof row?.["subject"] === "string" ? row["subject"] : data.subject,
    body: typeof row?.["body"] === "string" ? row["body"] : data.body,
  };
};

export function KickoffPackets({ requisitionId }: { requisitionId: string }) {
  const key = ["packet-set", requisitionId] as const;
  const ackKey = ["panel-ack", requisitionId] as const;
  const q = useSettledRead(key, () => packetReadinessCheck({ requisition_id: requisitionId }));
  const d = q.data as ReadinessV2 | undefined;
  const projection: KickoffProjection = d?.projection ?? "full";
  const ack = useSettledRead(ackKey, () => panelAckSummary({ requisition_id: requisitionId }), { enabled: !!d && projection !== "finalized_panel" });
  const [dialog, setDialog] = useState(false);
  const [change, setChange] = useState<null | { roundDefId?: string; acknowledgmentId?: string }>(null);
  const draft = useSettledRead(["kickoff-draft", requisitionId], () => packetDraftKickoff({ requisition_id: requisitionId }), { enabled: dialog });
  const inval = [key, ackKey, ["requisition", requisitionId], ["packets"], ["today"]] as const;
  const review = useWrite((i: { requisition_id: string; expected_version?: number }, k: string) => packetReview(i, k), inval);
  const release = useWrite((i: { requisition_id: string; expected_version?: number }, k: string) => packetRelease(i, k), inval);
  const [justReleased, setJustReleased] = useState(false);

  const rows: KickoffRow[] = (d?.grid ?? []).filter((g) => g.packet_status !== "superseded").map((g) => ({
    roundDefId: g.round_def_id, roundKind: g.round_kind, roundLabel: g.round_label,
    interviewerId: g.interviewer_id, interviewerName: g.interviewer_name,
    values: g.assesses, skillNumbers: g.skill_numbers,
    packetId: g.packet_id, packetVersion: g.packet_version, packetStatus: g.packet_status,
  }));
  const blockers = d?.blockers ?? d?.reasons ?? [];
  const canChange = projection === "full" || projection === "plan";
  // a row's hotspot is its place in the summary: an acknowledgment id carries "_", which the hotspot grammar refuses
  const ackRows: AckSummaryRow[] | null = ack.data ? ack.data.rows.map((r, i) => ({
    key: r.acknowledgment_id, name: r.name ?? "", round: r.round_label ?? "",
    status: r.nonresponse ? "nonresponse" : ACK_STATUS[r.status] ?? "pending",
    respondedAt: r.responded_at ?? undefined,
    canPanelChange: canChange,
    onPanelChange: canChange ? () => setChange({ roundDefId: r.round_def_id, acknowledgmentId: r.acknowledgment_id }) : undefined,
    hs: `ack.panel-change.${i + 1}`,
  })) : null;
  const version = d?.release?.version;
  const expected = version === undefined ? {} : { expected_version: version };
  const recipients: ReleaseRecipient[] = (draft.data?.recipients ?? []).map((r) => ({ id: String(r["person_id"] ?? r["id"] ?? r["name"]), name: String(r["name"] ?? "") }));
  const err = review.error ?? release.error;

  return (
    <McRoot>
      <KickoffPacketsView
        projection={projection}
        rows={rows}
        blockers={blockers}
        release={d?.release ?? null}
        ackRows={ackRows}
        loading={q.isLoading}
        onReview={() => void review.mutateAsync({ requisition_id: requisitionId, ...expected }).catch(() => undefined)}
        reviewing={review.isPending}
        onRelease={() => setDialog(true)}
        onPanelChange={canChange ? (roundDefId) => setChange({ roundDefId }) : undefined}
        refused={err && !dialog ? errorText(err) : undefined}
      />
      <ReleaseDialog
        open={dialog}
        
        onClose={() => { setDialog(false); setJustReleased(false); }}
        onRelease={projection === "full" ? () => void release.mutateAsync({ requisition_id: requisitionId, ...expected })
          .then(() => setJustReleased(true)).catch(() => undefined) : undefined}
        busy={release.isPending}
        blocked={blockers[0] ? { reason: blockers[0] } : undefined}
        refused={release.error ? errorText(release.error) : undefined}
        recipients={recipients}
        draft={composedDraft(draft.data)}
        onRegenerate={() => void draft.refetch()}
        regenerating={draft.isFetching}
        released={justReleased ? { version: d?.release?.email?.version } : null}
      />
      {change ? (
        <PanelChangeSlideOver
          requisitionId={requisitionId}
          roundDefId={change.roundDefId}
          acknowledgmentId={change.acknowledgmentId}
          open
          onClose={() => setChange(null)}
          onSubmitted={() => setChange(null)}
        />
      ) : null}
    </McRoot>
  );
}
